import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, UserCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  in: { label: "Binnen", color: "text-svj-cyan" },
  done: { label: "Klaar", color: "text-emerald-400" },
  not_seen: { label: "Nog niet gezien", color: "text-slate-500" },
};

function localDateIso() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function useCompanionPresence(eventId, authAxios, pollInterval = 30000) {
  const [days, setDays] = useState([]);
  const [selectedDayId, setSelectedDayId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [routeGroups, setRouteGroups] = useState([]);
  const [routeGroupId, setRouteGroupId] = useState("");

  useEffect(() => {
    let cancelled = false;
    setDays([]);
    setSelectedDayId("");
    setData(null);
    setRouteGroups([]);
    setRouteGroupId("");
    setLoading(!!eventId);
    if (!eventId) return () => { cancelled = true; };

    (async () => {
      try {
        const { data: response } = await authAxios.get(`/admin/events/${eventId}/days`);
        if (cancelled) return;
        const items = response.items || [];
        setDays(items);
        setSelectedDayId((items.find((item) => item.date === localDateIso()) || items[0])?.id || "");
        if (items.length === 0) setLoading(false);
      } catch (error) {
        if (!cancelled) {
          setLoading(false);
          toast.error(error?.response?.data?.detail || error.message);
        }
      }
    })();
    // Route-group filter (Sprint 03) — best-effort: an org without route groups yet
    // (or an older event) simply gets an empty list back and no filter is shown.
    (async () => {
      try {
        const { data: response } = await authAxios.get(`/admin/events/${eventId}/route-groups`);
        if (!cancelled) setRouteGroups(response.items || []);
      } catch {
        if (!cancelled) setRouteGroups([]);
      }
    })();

    return () => { cancelled = true; };
  }, [authAxios, eventId]);

  const refresh = useCallback(async () => {
    if (!eventId || !selectedDayId) return;
    try {
      const { data: response } = await authAxios.get(`/admin/events/${eventId}/checkins`, {
        params: { event_day_id: selectedDayId, route_group_id: routeGroupId || undefined },
      });
      setData(response);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  }, [authAxios, eventId, selectedDayId, routeGroupId]);

  useEffect(() => {
    if (!selectedDayId) return undefined;
    setLoading(true);
    setData(null);
    refresh();
    const timer = setInterval(refresh, pollInterval);
    return () => clearInterval(timer);
  }, [pollInterval, refresh, selectedDayId]);

  return { days, selectedDayId, setSelectedDayId, data, loading, refresh, routeGroups, routeGroupId, setRouteGroupId };
}

export default function CompanionPresence({ eventId, authAxios }) {
  const { days, selectedDayId, setSelectedDayId, data, loading, routeGroups, routeGroupId, setRouteGroupId } =
    useCompanionPresence(eventId, authAxios);
  const [search, setSearch] = useState("");

  // Client-side only — the full list is already fetched per day/route-group
  // filter, so a name/code search needs no new endpoint or round-trip.
  const visibleItems = useMemo(() => {
    const items = data?.items || [];
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const haystack = `${item.holder_name || ""} ${item.code || ""} ${item.ticket_name || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [data, search]);

  return (
    <div data-testid="companion-presence">
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center gap-2">
          <UserCheck className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Presentielijst</div>
          {data?.day && (
            <span className="text-xs text-slate-400">
              — dag {data.day.day_number}{data.day.label ? ` (${data.day.label})` : ""}
            </span>
          )}
        </div>

        {days.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Eventdagen">
            {days.map((day) => (
              <button
                key={day.id}
                type="button"
                role="tab"
                aria-selected={selectedDayId === day.id}
                onClick={() => setSelectedDayId(day.id)}
                data-testid={`presence-day-tab-${day.id}`}
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                  selectedDayId === day.id
                    ? "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white"
                    : "bg-svj-bg border border-svj-border text-slate-300 hover:text-white"
                }`}
              >
                Dag {day.day_number}{day.label ? ` — ${day.label}` : ""}
              </button>
            ))}
          </div>
        )}

        {routeGroups.length > 0 && (
          <label className="block max-w-xs">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Routekeuze</span>
            <select
              value={routeGroupId}
              onChange={(e) => setRouteGroupId(e.target.value)}
              data-testid="presence-route-group-filter"
              className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            >
              <option value="">Alle</option>
              {routeGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                  {g.distance_km ? ` (${g.distance_km} km)` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {loading && <div className="text-sm text-slate-400">Laden…</div>}

      {!loading && days.length === 0 && (
        <div className="text-sm text-slate-400">Voeg eerst een dag toe om de presentielijst te kunnen zien.</div>
      )}

      {!loading && data?.day && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {["in", "done", "not_seen"].map((status) => (
              <div key={status} className="rounded-xl border border-svj-border bg-svj-bg/60 p-3 text-center">
                <div className={`text-2xl font-extrabold ${STATUS_META[status].color}`} data-testid={`a4d-presence-count-${status}`}>
                  {data.counts[status]}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                  {STATUS_META[status].label}
                </div>
              </div>
            ))}
          </div>

          {data.items.length === 0 ? (
            <div className="text-sm text-slate-400">Nog geen tickets voor dit event.</div>
          ) : (
            <>
              <label className="relative block mb-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Zoek op naam of ticketcode…"
                  data-testid="a4d-presence-search"
                  className="w-full h-9 pl-9 pr-3 rounded-lg bg-svj-bg border border-svj-border text-white text-xs placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                />
              </label>

              {visibleItems.length === 0 ? (
                <div className="text-sm text-slate-400">Geen deelnemers gevonden voor "{search}".</div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto space-y-1.5 pr-1">
                  {visibleItems.map((item) => (
                    <div key={item.ticket_id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-svj-bg/60 border border-svj-border" data-testid={`a4d-presence-row-${item.ticket_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">{item.holder_name || item.code}</div>
                        <div className="text-[10px] text-slate-500 truncate">
                          <span className="font-mono text-slate-400">{item.code}</span>
                          {item.ticket_name && ` · ${item.ticket_name}`}
                          {item.route_group_name && ` · ${item.route_group_name}`}
                          {item.medal_number && ` · Medaille ${item.medal_number}`}
                        </div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-widest font-bold shrink-0 ${STATUS_META[item.status].color}`}>
                        {STATUS_META[item.status].label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
